import React, { useEffect, useState } from 'react';
import { Typography, Grid, Paper, Box, CircularProgress } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import API_URL from '../../settings';
import { useAuth } from '../../context/AuthContext';

const KpiCard = ({ title, value, icon }) => (
    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%', borderRadius: 3, boxShadow: 3 }}>
        {icon && <Box sx={{ mr: 2, color: 'primary.main' }}>{icon}</Box>}
        <Box>
            <Typography color="text.secondary">{title}</Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>{value}</Typography>
        </Box>
    </Paper>
);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

function EconomicDashboard() {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const patientsUrl =
                    user.role === 'VOLUNTEER'
                        ? `${API_URL}/patients?volunteer_id=${user.id}`
                        : `${API_URL}/patients?hospital_id=${user.hospital_id}`;

                const response = await fetch(patientsUrl);
                const data = await response.json();
                setPatients(data);
            } catch (error) {
                console.error("Failed to fetch patient data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
    }

    // --- Calculations for Dashboard ---
    const surveyedCount = patients.filter(p => p.economy && p.economy.status).length;
    const vulnerableCount = patients.filter(p =>
        p.economy?.status === 'รายได้น้อย' ||
        p.economy?.status === 'ไม่มี' ||
        p.risk_group === 'ดูแลพิเศษ' ||
        p.risk_group === 'ฉุกเฉิน'
    ).length;
    const welfareCount = patients.filter(p => p.economy?.welfare).length;

    const incomeStatusData = patients.reduce((acc, p) => {
        const status = p.economy?.status || 'ไม่ระบุ';
        const found = acc.find(item => item.name === status);
        if (found) {
            found.value += 1;
        } else {
            acc.push({ name: status, value: 1 });
        }
        return acc;
    }, []);

    const incomeSourceData = patients.reduce((acc, p) => {
        const source = p.economy?.source || 'ไม่ระระบุ';
        if (source === 'ไม่ระบุ' || source === '') return acc;
        const found = acc.find(item => item.name === source);
        if (found) {
            found.value += 1;
        } else {
            acc.push({ name: source, value: 1 });
        }
        return acc;
    }, []);


    return (
        <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                ภาพรวมข้อมูลเศรษฐกิจครัวเรือน
            </Typography>
            <Grid container spacing={2}>
                {/* KPI Cards */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <KpiCard title="ผู้มีข้อมูลเศรษฐกิจ" value={surveyedCount} icon={<PeopleAltIcon fontSize="large" />} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <KpiCard title="กลุ่มเปราะบาง" value={vulnerableCount} icon={<AccessibilityNewIcon fontSize="large" />} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <KpiCard title="ผู้ได้รับสวัสดิการ" value={welfareCount} icon={<AccountBalanceWalletIcon fontSize="large" />} />
                </Grid>

                {/* Charts */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 5, height: 450, borderRadius: 3, boxShadow: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>สถานะรายได้</Typography>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={incomeStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#8884d8" label>
                                    {incomeStatusData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend layout="horizontal" verticalAlign="bottom" align="left" sx={{ mt: 2 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 5, height: 450, borderRadius: 3, boxShadow: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>แหล่งที่มาของรายได้</Typography>
                        <ResponsiveContainer>
                            <BarChart data={incomeSourceData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="value" fill="#82ca9d" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default EconomicDashboard;
